/**
 * Orders Page - View and manage open orders
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Search, Filter, Clock, DollarSign, User, 
  MoreVertical, Printer, Send, CheckCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { PaymentParts, SplitBadge } from '@/components/payments/SplitPayments';
import { DARK_TONES, orderStatus, paymentStatus } from '@/lib/orderStatus';
import { formatDateTime, parseApiTime } from '@/lib/restaurantTime';

const API = process.env.REACT_APP_BACKEND_URL || '';
const orderNo = (o) => o.order_number || `#${o.id}`;

// The order's real state (orders.status) and its money (orders.payment_status), one vocabulary
// (lib/orderStatus); times on the restaurant's clock (lib/restaurantTime), never the browser's.
const FILTERS = ['awaiting_payment', 'confirmed', 'preparing', 'ready', 'completed', 'cancelled'];

function StatusBadges({ order }) {
  const s = orderStatus(order.status);
  const p = paymentStatus(order.payment_status);
  return (
    <span className="flex items-center gap-1.5">
      <Badge className={DARK_TONES[s.tone]} data-testid="pos-order-status">{s.label}</Badge>
      {order.payment_status && <Badge className={DARK_TONES[p.tone]} data-testid="pos-order-payment">{p.label}</Badge>}
    </span>
  );
}

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const { getToken } = useAuth();

  // status: the server-side filter; the load and the 30 s poll ask for every order
  // (the list below filters by statusFilter itself), "Try again" asks for the chosen one.
  const fetchOrders = useCallback(async (status = 'all') => {
    try {
      const response = await axios.get(`${API}/api/orders`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        params: { status: status !== 'all' ? status : undefined }
      });
      setOrders(response.data || []);
      setLoadError(false);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Could not load orders', { id: 'pos-orders-load' });
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    const load = () => fetchOrders();
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getTimeAgo = (date) => {
    const at = parseApiTime(date);
    if (!at) return '-';
    const minutes = Math.floor((Date.now() - at.getTime()) / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    return `${Math.floor(minutes / 60)}h ${minutes % 60}m ago`;
  };

  const filteredOrders = orders.filter(order => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!orderNo(order).toLowerCase().includes(query) &&
          !order.customer_name?.toLowerCase().includes(query)) {
        return false;
      }
    }
    if (statusFilter !== 'all' && order.status !== statusFilter) {
      return false;
    }
    return true;
  });

  return (
    <div className="h-screen flex bg-slate-950">
      {/* Orders List */}
      <div className="flex-1 flex flex-col border-r border-slate-800">
        {/* Header */}
        <div className="p-4 border-b border-slate-800">
          <div className="flex items-center gap-4 mb-4">
            <h1 className="text-2xl font-bold text-white">Orders</h1>
            <Badge className="bg-emerald-600/20 text-emerald-400">
              {filteredOrders.length} orders
            </Badge>
          </div>
          
          {/* Search and Filter */}
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <Input
                placeholder="Search orders..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-slate-800 border-slate-700 text-white"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
            >
              <option value="all">All Status</option>
              {FILTERS.map(f => <option key={f} value={f}>{orderStatus(f).label}</option>)}
            </select>
          </div>
        </div>

        {/* Orders List */}
        <ScrollArea className="flex-1">
          <div className="p-4 space-y-3">
            {filteredOrders.map((order) => {
              return (
                <button
                  key={order.id}
                  data-testid={`pos-order-${order.id}`}
                  onClick={() => setSelectedOrder(order)}
                  className={`w-full text-left p-4 rounded-xl border transition-colors ${
                    selectedOrder?.id === order.id
                      ? 'bg-slate-700 border-emerald-500'
                      : 'bg-slate-800 border-slate-700 hover:border-slate-600'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <span className="text-lg font-bold text-white">{orderNo(order)}</span>
                      {order.table_number && (
                        <Badge className="ml-2 bg-slate-600/50 text-slate-300">
                          Table {order.table_number}
                        </Badge>
                      )}
                    </div>
                    <StatusBadges order={order} />
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-slate-400 mb-2">
                    <span className="flex items-center gap-1" data-testid="pos-order-customer">
                      <User className="w-4 h-4" />
                      {order.customer_name || 'Guest'}
                    </span>
                    <span className="flex items-center gap-1" title={formatDateTime(order.created_at, order.restaurant_timezone)}>
                      <Clock className="w-4 h-4" />
                      {getTimeAgo(order.created_at)}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500 flex items-center gap-2">
                      {order.items?.length || 0} items
                      <SplitBadge split={order.split} dark />
                    </span>
                    <span className="text-emerald-400 font-bold">
                      {formatCurrency(order.total)}
                    </span>
                  </div>
                </button>
              );
            })}

            {loadError && (
              <div className="text-center py-6 text-red-500" data-testid="pos-orders-load-error">
                <p>Could not load orders.</p>
                <Button variant="outline" size="sm" className="mt-2" onClick={() => fetchOrders(statusFilter)}>Try again</Button>
              </div>
            )}

            {filteredOrders.length === 0 && !loadError && (
              <div className="text-center py-12 text-slate-500">
                <p>No orders found</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Order Detail */}
      <div className="w-96 flex flex-col">
        {selectedOrder ? (
          <>
            {/* Order Header */}
            <div className="p-4 border-b border-slate-800">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-xl font-bold text-white">{orderNo(selectedOrder)}</h2>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="text-slate-400">
                      <MoreVertical className="w-5 h-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
                    <DropdownMenuItem className="text-slate-300">
                      <Printer className="w-4 h-4 mr-2" />
                      Print Receipt
                    </DropdownMenuItem>
                    <DropdownMenuItem className="text-slate-300">
                      <Send className="w-4 h-4 mr-2" />
                      Send to Kitchen
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              
              <StatusBadges order={selectedOrder} />
            </div>

            {/* Order Info */}
            <div className="p-4 border-b border-slate-800 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Customer</span>
                <span className="text-white" data-testid="pos-order-detail-customer">{selectedOrder.customer_name || 'Guest'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Type</span>
                <span className="text-white capitalize">{selectedOrder.order_type?.replace('_', ' ')}</span>
              </div>
              {selectedOrder.table_number && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Table</span>
                  <span className="text-white">{selectedOrder.table_number}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Time</span>
                <span className="text-white" data-testid="pos-order-time">{formatDateTime(selectedOrder.created_at, selectedOrder.restaurant_timezone)}</span>
              </div>
            </div>

            {/* Order Items */}
            <ScrollArea className="flex-1">
              <div className="p-4 space-y-3">
                <h3 className="text-sm font-semibold text-slate-400">Items</h3>
                {selectedOrder.items?.map((item, index) => (
                  <div key={index} className="flex justify-between items-center p-3 bg-slate-800 rounded-lg">
                    <div>
                      <span className="text-white font-medium">
                        {item.quantity}x {item.name}
                      </span>
                    </div>
                    <span className="text-slate-400">{formatCurrency(item.total_price ?? (item.price * item.quantity))}</span>
                  </div>
                ))}
                {selectedOrder.payments?.length > 0 && (
                  <div className="pt-2 space-y-2" data-testid="pos-order-payments">
                    <h3 className="text-sm font-semibold text-slate-400 flex items-center gap-2">Payments <SplitBadge split={selectedOrder.split} dark /></h3>
                    <PaymentParts payments={selectedOrder.payments} timezone={selectedOrder.restaurant_timezone} dark />
                  </div>
                )}
              </div>
            </ScrollArea>

            {/* Order Total */}
            <div className="p-4 border-t border-slate-800">
              <div className="flex justify-between text-xl font-bold">
                <span className="text-white">Total</span>
                <span className="text-emerald-400">{formatCurrency(selectedOrder.total)}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="p-4 border-t border-slate-800">
              <Button className="w-full h-12 bg-emerald-600 hover:bg-emerald-700">
                <CheckCircle className="w-5 h-5 mr-2" />
                Mark as Complete
              </Button>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-slate-500">
            <p>Select an order to view details</p>
          </div>
        )}
      </div>
    </div>
  );
}
